import { Grid, useMediaQuery } from "@mui/material";
import classNames from "classnames";
import { getClassNameVariant } from "components/DIP/helper";
import { getFlexBasis } from "components/DIP/lib/helper";
import { useStickyDIPAction } from "components/StickyDIP/hooks/useStickyDipAction";
import { useEnvContext } from "contexts";
import { IShopPurchaseOffer } from "contexts/WebStoreProvider/types";
import { buyItAllAssets } from "assets/cdn";
import { FeaturesFlags } from "helpers";
import { useAppSelector } from "hooks";
import { useImagePath } from "hooks/useImagePath";
import { DipButton } from "interfaces";
import { Fragment, useState } from "react";
import { selectStickyDIPState, selectWebStoreState } from "redux/slices";
// import { CardFinalMessage } from "styles/general_styles";
import { CrossedOutText } from "styles/general_styles";
import { getDisplayRewards } from "helpers/getDisplayRewards";
import { CrownIcon, CrownIconV2 } from "utils/svgIcons";
import {
	BestValueBadge,
	Button,
	BuyItAllButton,
	BuyItAllItem,
	BuyItAllStyled,
	CustomLoyaltyPointsV2,
	FingerPointer,
	Footer,
	FooterGridItem,
	ItemImage,
	Items,
	LockedOffer,
	LoyaltyPoints,
	LoyaltyValue,
	OfferGridItem,
	RewardAmount,
	RewardItem
} from "./styles";
import { sortBundleRewards } from "helpers/rewardSort";

interface BuyItAllProps {
	data: Omit<
		IShopPurchaseOffer,
		"onlyNonPayers" | "priority" | "purchaseButtonBannerText" | "bannerText"
	>[];
}

export const BuyItAll: React.FC<BuyItAllProps> = ({ data }: BuyItAllProps) => {
	const { getImage } = useImagePath("cdn");
	const redBadge = getImage(buyItAllAssets.badge);
	const fingerPointerIcon = getImage(buyItAllAssets.fingerPointer);
	const { onStickyAction } = useStickyDIPAction();
	const { isFeatureFlagEnabled } = useEnvContext();
	const isLowWidth = useMediaQuery("(max-width:733px)");
	const [lockOffers, setLockOffers] = useState<number>(-1);
	const { claimRewardProcess } = useAppSelector(selectStickyDIPState);
	const { rewards, rewardTypes } = useAppSelector(selectWebStoreState).store;
	// Temporarily hidden per product request; restore this state with the JSX below
	// to re-enable the Poi's Bounty messaging.
	// const { canContribute: isPoisActive } = useAppSelector(
	// 	selectPoisBountyV3State
	// );
	const isMobile = isLowWidth;
	const { claiming } = claimRewardProcess;

	if (!data) {
		return <Fragment />;
	}

	const allOffersButLast = data.slice(0, -1);
	const lastItem = data.slice(-1)[0];

	const onBuyClick = (id: string, price: number, rewardId: string) => {
		onStickyAction(id, rewardId, price);
	};

	const onOfferClick = (
		id: string,
		price: number,
		rewardId: string,
		index: number
	) => {
		if (lockOffers !== -1 && lockOffers < 3) {
			onBuyClick(id, price, rewardId);
		} else {
			setLockOffers(index);
		}
	};

	const onCancelLock = (index: number) => {
		setLockOffers(index);
	};

	const onBuyItAll = (id: string, price: number, rewardId: string) => {
		setLockOffers(3);
		onBuyClick(id, price, rewardId);
	};

	return (
		<BuyItAllStyled className={getClassNameVariant(data?.length as number)}>
			<Grid container spacing={1} justifyContent="center" alignItems="flex-end">
				{allOffersButLast.map(
					(
						{
							rewardId,
							price,
							loyaltyPointReward,
							loyaltyPointCrossout,
							id,
							badgeText,
							badgeImageUrl
						},
						i
					) => {
						const items = getDisplayRewards(rewardId, {
							rewards,
							rewardTypes
						});
						return (
							<OfferGridItem item key={id} position="relative">
								{lockOffers !== -1 && lockOffers !== i && (
									<div onClick={() => onCancelLock(i)}>
										<LockedOffer />
									</div>
								)}
								<BuyItAllItem
									tier={i}
									className={classNames([
										getClassNameVariant(data.length as number),
										loyaltyPointReward && "loyalty-reward",
										"item-reward"
									])}
								>
									{badgeText && badgeImageUrl && (
										<BestValueBadge
											className={`${
												lockOffers !== -1 && lockOffers !== i && "blocked-offer"
											}`}
											backgroundImage={badgeImageUrl}
										>
											<span>{badgeText}</span>
										</BestValueBadge>
									)}
									<Items
										tier={i}
										className={classNames([
											getClassNameVariant(data.length as number),
											"bundle-rewards-item"
										])}
									>
										{sortBundleRewards(items).map(
											({ imageUrl, description }, key) => {
												const flexBasis = getFlexBasis(
													data as unknown as DipButton[],
													items,
													isMobile
												);

												return (
													<RewardItem key={key} width={flexBasis + "%"}>
														<ItemImage src={imageUrl} alt="" />
														<RewardAmount className="description">
															{description}
														</RewardAmount>
													</RewardItem>
												);
											}
										)}
									</Items>

									<Footer
										className={classNames([
											loyaltyPointReward && "loyalty-reward",
											getClassNameVariant(data.length as number)
										])}
									>
										<Grid container>
											<Grid container spacing={0.5} alignItems="center">
												<FooterGridItem item>
													{loyaltyPointReward && (
														<>
															{isFeatureFlagEnabled(FeaturesFlags.UI_V2) ? (
																<CustomLoyaltyPointsV2>
																	{loyaltyPointCrossout && (
																		<CrossedOutText>
																			Earn{" "}
																			<CrownIconV2
																				height="0.9em"
																				width="auto"
																			/>
																			{Number(
																				loyaltyPointCrossout
																			)?.toLocaleString("en-US")}
																		</CrossedOutText>
																	)}
																	<LoyaltyValue>
																		Earn{" "}
																		<CrownIconV2 height="1.1em" width="auto" />
																		{loyaltyPointReward?.toLocaleString(
																			"en-US"
																		)}
																	</LoyaltyValue>
																</CustomLoyaltyPointsV2>
															) : (
																<LoyaltyPoints>
																	<div>
																		Earn <CrownIcon width="1em" />
																		{loyaltyPointReward?.toLocaleString(
																			"en-US"
																		)}
																	</div>
																</LoyaltyPoints>
															)}
														</>
													)}
												</FooterGridItem>
												<FooterGridItem item position="relative">
													{lockOffers === i && (
														<FingerPointer
															src={fingerPointerIcon}
															alt="Press buy again to confirm"
														/>
													)}
													<Button
														disabled={claiming}
														onClick={() => onOfferClick(id, price, rewardId, i)}
													>
														{price} USD
													</Button>
												</FooterGridItem>
												{/*
													{isPoisActive && (
														<Grid item xs={12}>
															<CardFinalMessage
																color="#FBBD05"
																className="with-contrast"
																style={{ fontSize: "0.7em", textAlign: "center" }}
															>
																Contribute to Poi’s Bounty
															</CardFinalMessage>
														</Grid>
													)}
												*/}
											</Grid>
										</Grid>
									</Footer>
								</BuyItAllItem>
							</OfferGridItem>
						);
					}
				)}
			</Grid>
			<Grid item position="relative">
				{lastItem.badgeText && lastItem.badgeImageUrl && (
					<BestValueBadge
						className={`buy-all-button ${
							lockOffers !== -1 && lockOffers !== 3 && "blocked-offer"
						}`}
						backgroundImage={redBadge}
					>
						<span>{lastItem.badgeText}</span>
					</BestValueBadge>
				)}
				<BuyItAllButton
					disabled={claiming}
					onClick={() =>
						onBuyItAll(lastItem.id, lastItem.price, lastItem.rewardId)
					}
					themeColor="tertiary"
				>
					Buy All {lastItem.price} USD
				</BuyItAllButton>
			</Grid>
			{/* <BuyItAllFooterText>Only one purchase can be made</BuyItAllFooterText> */}
		</BuyItAllStyled>
	);
};
